import React, { useState, useEffect } from "react";
import log from "loglevel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { validPassword } from "util/formValidators";
import { usePrevious } from "lib/hooks";
import styles from "./PasswordStrengthMeter.module.scss";

type PasswordResultType = {
  score: number;
  password: string;
};

const PasswordStrengthMeter = ({
  password,
}: {
  password: string | undefined;
}) => {
  const zxcvbn = require("zxcvbn");

  const [passwordAnalysis, setPasswordAnalysis] = useState<
    PasswordResultType
  >();
  const [isPasswordOk, setIsPasswordOk] = useState<boolean>(false);
  const [iconColor, setIconColor] = useState<string>();

  const prevPassword: string | undefined = usePrevious(password);

  useEffect(() => {
    log.info("PasswordStrengthMeter useEffect()...");
    if (password && password !== prevPassword) {
      const passwordValidation: boolean = validPassword(password);
      setPasswordAnalysis(zxcvbn(password));
      passwordValidation ? setIsPasswordOk(true) : setIsPasswordOk(false);
      passwordValidation ? setIconColor("green") : setIconColor("red");
    }
  }, [password, isPasswordOk, passwordAnalysis, zxcvbn]);

  const createPasswordLabel = (result: PasswordResultType) => {
    switch (result.score) {
      case 0:
        if (result.password === "") {
          return "";
        }
        return "Weak";
      case 1:
        return "Weak";
      case 2:
        return "Fair";
      case 3:
        return "Good";
      case 4:
        if (validPassword(result.password)) {
          return "Strong";
        } else {
          return "Good";
        }
      default:
        return "Weak";
    }
  };

  return (
    <>
      {passwordAnalysis ? (
        <div className={styles["password-strength-meter"]}>
          <progress
            className={[
              styles["password-strength-meter__progress"],
              styles[
                `password-strength-meter__strength-${createPasswordLabel(
                  passwordAnalysis
                )}`
              ],
            ].join(" ")}
            value={
              passwordAnalysis && validPassword(passwordAnalysis.password)
                ? passwordAnalysis.score
                : passwordAnalysis.score - 1
            }
            max="4"
          />
          <br />
          <label className={styles["password-strength-meter__label"]}>
            <>
              <p>
                <strong>Password Strength:</strong>{" "}
                {createPasswordLabel(passwordAnalysis)}
              </p>
              {password && (
                <p>
                  <span style={{ color: iconColor }}>
                    {isPasswordOk ? (
                      <>
                        Password Approved{" "}
                        <FontAwesomeIcon icon={["far", "check-circle"]} />
                      </>
                    ) : (
                      <>
                        Password Not Approved{" "}
                        <FontAwesomeIcon icon={["far", "times-circle"]} />
                      </>
                    )}
                  </span>
                </p>
              )}
            </>
          </label>
        </div>
      ) : null}
    </>
  );
};

export default PasswordStrengthMeter;
